import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { siteConfig } from '../../data/mock';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  CheckCircle2,
  Loader2,
  Building2,
  User,
  AtSign,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Try to submit to backend
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
        toast.success(data.message || 'Mensagem enviada com sucesso!');
        
        // Reset after showing success
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            message: '',
          });
        }, 3000);
      } else {
        throw new Error(data.message || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      // Fallback to mailto if backend fails
      const subject = `Contato via Site - ${formData.name} (${formData.company})`;
      const body = `Nome: ${formData.name}\nEmpresa: ${formData.company}\nE-mail: ${formData.email}\nTelefone: ${formData.phone}\n\nMensagem:\n${formData.message}`;
      
      const mailtoLink = `mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
      
      toast.info('Redirecionando para seu cliente de e-mail...');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(siteConfig.whatsappMessage)}`,
      '_blank'
    );
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'E-mail',
      value: siteConfig.email,
      href: `mailto:${siteConfig.email}`,
    },
    {
      icon: Phone,
      label: 'Telefone',
      value: siteConfig.phone,
      href: `tel:${siteConfig.phone.replace(/\D/g, '')}`,
    },
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      value: siteConfig.phone,
      action: handleWhatsApp,
    },
    {
      icon: MapPin,
      label: 'Endereço',
      value: siteConfig.address,
    },
    {
      icon: Clock,
      label: 'Horário',
      value: siteConfig.workingHours,
    },
  ];

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#329780]/10 border border-[#329780]/20">
              <Send className="w-4 h-4 text-[#329780] mr-2" />
              <span className="text-[#329780] text-sm font-medium">Contato</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Vamos <span className="text-[#329780]">conversar</span>?
            </h1>

            <p className="text-lg text-slate-400 leading-relaxed">
              Estamos prontos para ajudar sua empresa a alcançar novos patamares.
              Entre em contato e agende uma consulta.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="bg-[#111920] border-[#1e293b]">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">
                    Envie sua mensagem
                  </h2>

                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-[#329780]/20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-[#329780]" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        Mensagem enviada!
                      </h3>
                      <p className="text-slate-400">
                        Recebemos sua mensagem e entraremos em contato em breve.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300 flex items-center">
                            <User className="w-4 h-4 mr-2 text-slate-500" />
                            Nome completo *
                          </label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Seu nome"
                            className="bg-[#0a0f14] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780] h-12"
                          />
                        </div>

                        {/* Company */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300 flex items-center">
                            <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                            Empresa *
                          </label>
                          <Input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            required
                            placeholder="Nome da empresa"
                            className="bg-[#0a0f14] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780] h-12"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Email */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300 flex items-center">
                            <AtSign className="w-4 h-4 mr-2 text-slate-500" />
                            E-mail *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="seu@email.com"
                            className="bg-[#0a0f14] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780] h-12"
                          />
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-slate-500" />
                            Telefone *
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="(00) 00000-0000"
                            className="bg-[#0a0f14] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780] h-12"
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center">
                          <Mail className="w-4 h-4 mr-2 text-slate-500" />
                          Mensagem *
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Como podemos ajudar?"
                          rows={5}
                          className="bg-[#0a0f14] border-[#1e293b] text-white placeholder:text-slate-500 focus:border-[#329780] resize-none"
                        />
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#329780] hover:bg-[#287a68] text-white h-12 text-base font-medium"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar mensagem
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-[#111920] border-[#1e293b]">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">
                    Informações de contato
                  </h2>

                  <div className="space-y-6">
                    {contactInfo.map((item, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="w-10 h-10 rounded-lg bg-[#329780]/10 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-[#329780]" />
                        </div>
                        <div>
                          <p className="text-slate-500 text-sm">{item.label}</p>
                          {item.href ? (
                            <a
                              href={item.href}
                              className="text-white hover:text-[#329780] transition-colors duration-200"
                            >
                              {item.value}
                            </a>
                          ) : item.action ? (
                            <button
                              onClick={item.action}
                              className="text-white hover:text-[#329780] transition-colors duration-200"
                            >
                              {item.value}
                            </button>
                          ) : (
                            <p className="text-white">{item.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp CTA */}
              <Card className="bg-[#111920] border-[#1e293b] overflow-hidden">
                <CardContent className="p-6 sm:p-8 relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#25D366]/10 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-[#25D366]/20 flex items-center justify-center mb-4">
                      <MessageCircle className="w-6 h-6 text-[#25D366]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Prefere WhatsApp?
                    </h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Resposta rápida em horário comercial.
                    </p>
                    <Button
                      onClick={handleWhatsApp}
                      className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Chamar no WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
